'use client';

import type { FeatureTabItem, FeatureTabsProps } from '@/core/types';

import React, { useState } from 'react';
import { m, AnimatePresence } from 'framer-motion';

import { Iconify } from '@/components/iconify';
import { Lordicon } from '@/components/lordicon';
import { Box, Image, Button, Container, Typography } from '@/components/ui';

import { RouterLink } from '@/routes/components';

export default function FeatureTabsV1({ items, className }: FeatureTabsProps) {
  const [activeTab, setActiveTab] = useState(items?.[0]?.id || '');
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);

  const handleTabClick = (tabId: string) => setActiveTab(tabId);
  const handleAccordionToggle = (itemId: string) =>
    setOpenAccordion(openAccordion === itemId ? null : itemId);

  const activeItem = items?.find((item) => item.id === activeTab);
  const i = items?.findIndex((item) => item.id === activeTab);
  const itemWidthPercent = 100 / items?.length;
  const progressPercent = i * itemWidthPercent + itemWidthPercent / 2;

  return (
    <div className={`relative w-full bg-white py-12 md:py-20 ${className}`}>
      <Container>
        {/* Desktop Tabs */}
        <div className="hidden -mt-[110px] lg:block z-10">
          <Box className="grid grid-cols-[repeat(4,_minmax(150px,_1fr))] gap-4 border-gray-200 mb-16 xl:mb-24">
            {items?.map((item) => (
              <ItemButton key={item.id} item={item} onClick={handleTabClick} active={activeTab} />
            ))}
          </Box>

          <Box className="relative overflow-hidden pt-4 mb-12 xl:mb-20">
            <Box className="z-10 relative h-[9px] w-full bg-[linear-gradient(90deg,#ECCDFF_0%,#77E6BC_100%)] rounded-full border-t-2 border-white" />
            <Box
              className="absolute w-6 h-6 -bottom-[4px] bg-emerald-400 rounded-full shadow-md transition-all duration-300"
              style={{ left: `calc(${progressPercent}% - 16px)` }}
            />
          </Box>

          <AnimatePresence mode="wait">
            {activeItem && (
              <m.div
                key={activeItem.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
              >
                <Box className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(150px,400px)] xl:grid-cols-[1fr_minmax(150px,700px)] gap-20 xl:gap-28 items-center">
                  <ItemContent item={activeItem} />
                </Box>
              </m.div>
            )}
          </AnimatePresence>
        </div>

        {/* Mobile Accordion */}
        <div className="flex flex-col gap-4 -mt-[80px] z-10 lg:hidden">
          {items?.map((item) => (
            <Box key={item.id} className="flex flex-col">
              <ItemButton item={item} onClick={handleAccordionToggle} active={openAccordion} />

              <AnimatePresence initial={false}>
                {openAccordion === item.id && (
                  <m.div
                    key="accordion"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <Box className="p-6">
                      <ItemContent item={item} />
                    </Box>
                  </m.div>
                )}
              </AnimatePresence>
            </Box>
          ))}
        </div>
      </Container>
    </div>
  );
}

interface ItemButtonProps {
  item: FeatureTabItem;
  onClick: (itemId: string) => void;
  active: string | null;
}

const ItemButton: React.FC<ItemButtonProps> = ({ item, onClick, active }) => (
  <button
    className={`cursor-pointer flex items-center border-3 border-white text-gray-600 bg-white rounded-4xl shadow-box flex-col px-6 py-4 xl:py-12 transition-colors hover:!border-primary ${
      active === item.id ? '!border-primary' : ''
    }`}
    onClick={() => onClick(item.id)}
  >
    <Box className="flex flex-col items-center">
      <Lordicon icon={item.icon} size={56} className="mb-2" />
      <Typography variant="body1" className="text-center text-lg lg:text-sm xl:text-lg font-medium">
        {item.title}
      </Typography>
    </Box>

    {/* Animated Chevron for mobile */}
    <m.div
      className="mt-2 lg:hidden"
      animate={{ rotate: active === item.id ? 180 : 0 }}
      transition={{ duration: 0.3 }}
    >
      <Iconify icon="lucide:chevron-down" width={24} height={24} />
    </m.div>
  </button>
);

interface ItemContentProps {
  item: FeatureTabItem;
}

const ItemContent: React.FC<ItemContentProps> = ({ item }) => (
  <>
    <Box>
      <Typography variant="h3" className="mb-3 text-primary text-2xl">
        {item.content.title}
      </Typography>
      <Typography
        variant="body1"
        paragraph
        className="text-gray-600 text-lg mb-8 xl:mb-12 font-medium"
      >
        {item.content.description}
      </Typography>

      <Box className="mb-8 xl:mb-11">
        {item.content.benefits.map((benefit, index) => (
          <Box key={index} className="flex items-start p-2.5">
            <Iconify icon="lucide:check" className="text-primary mr-2" width={22} />
            <Typography variant="body1">{benefit}</Typography>
          </Box>
        ))}
      </Box>

      {item.content.button && (
        <Button
          component={RouterLink}
          href={item.content.button.link}
          variant="outlined"
          color="primary"
          className="font-medium text-md"
        >
          {item.content.button.text}
        </Button>
      )}
    </Box>

    <Box className="hidden lg:flex justify-center">
      <Image
        src={item.content.image.url}
        alt={item.content.image.alt}
        className="w-full h-auto rounded-tl-4xl rounded-br-4xl"
        loading="lazy"
        width={920}
        height={650}
        objectFit="contain"
      />
    </Box>
  </>
);
